const toNumber = (value) => {
  if (value === undefined || value === null || String(value).trim() === '') return NaN;
  return Number(String(value).replace(/\s/g, '').replace(',', '.'));
};
const round = (value) => Math.round((value + Number.EPSILON) * 100) / 100;
const netPrice = (price, rate) => (rate === undefined ? toNumber(price)
  : round(toNumber(price) / (1 + toNumber(rate) / 100)));
const validItem = (item) => item && (item.item_id || item.item_name)
  && Number.isFinite(item.price) && item.price >= 0
  && Number.isFinite(item.quantity) && item.quantity > 0;
const sumItems = (items) => round(items.reduce((sum, item) => sum + item.price * item.quantity, 0));

// This observes browser dispatch, not guaranteed acceptance by Google.
export const captureGa4PurchaseContext = (fromDispatch = false) => {
  const node = document.querySelector('[data-order-list]');
  if (!node?.dataset.ga4CaptureToken || !/^G-[A-Z0-9]+$/.test(node.dataset.ga4MeasurementId || '')) return;
  const granted = () => String(document.cookie || '').split(';').some((cookie) => cookie.trim() === '_cookieSettingsStats=granted');
  if (!granted() || node.dataset.ga4CapturePending === '1' || node.dataset.ga4CaptureDone === '1') return;
  const eligibilityKey = `ga4CaptureEligible_${node.dataset.ga4CaptureToken}`;
  if (fromDispatch) {
    node.dataset.ga4CaptureEligible = '1';
    try { sessionStorage.setItem(eligibilityKey, '1'); } catch (error) { /* Current page still works. */ }
  }
  let eligible = node.dataset.ga4CaptureEligible === '1';
  try { eligible = eligible || sessionStorage.getItem(eligibilityKey) === '1'; } catch (error) { /* No replay without evidence. */ }
  if (!eligible) return;
  node.dataset.ga4CapturePending = '1';
  const timeout = setTimeout(() => { node.dataset.ga4CapturePending = ''; }, 5000);
  let called = false;
  const callback = (clientId) => {
    if (called) return;
    called = true;
    clearTimeout(timeout);
    if (!granted() || !/^[0-9]+\.[0-9]+$/.test(String(clientId)) || String(clientId).length > 80) {
      node.dataset.ga4CapturePending = ''; return;
    }
    const body = new URLSearchParams({ order_id: node.dataset.ga4CaptureOrder, token: node.dataset.ga4CaptureToken, client_id: String(clientId) });
    fetch('/order/recordGa4Purchase', { method: 'POST', credentials: 'same-origin', body, keepalive: true })
      .then((response) => { if (response.ok) node.dataset.ga4CaptureDone = '1'; })
      .catch(() => {})
      .finally(() => { node.dataset.ga4CapturePending = ''; });
  };
  try {
    const gtag = globalThis.gtag || function command() {
      globalThis.dataLayer = globalThis.dataLayer || [];
      globalThis.dataLayer.push(arguments);
    };
    gtag('get', node.dataset.ga4MeasurementId, 'client_id', callback);
  } catch (error) { clearTimeout(timeout); node.dataset.ga4CapturePending = ''; }
};

const getBasketData = () => {
  const node = document.querySelector('[data-basket-list]');
  const items = node ? [...node.querySelectorAll('[data-product-info]')].map(({ dataset }) => {
    // Legacy markup can remain in an open tab during a deployment.
    const fields = (dataset.productInfo || '').split(', ');
    const quantity = fields.pop();
    const price = fields.pop();
    const id = fields.shift();
    return {
      key: dataset.ga4Key || dataset.itemId || id,
      item_id: dataset.itemId || id,
      item_name: dataset.itemName ?? fields.join(', '),
      price: netPrice(dataset.price ?? price, dataset.taxRate),
      quantity: toNumber(dataset.quantity ?? quantity),
    };
  }) : [];
  return {
    node, currency: node?.dataset.ga4Currency, coupon: node?.dataset.ga4Coupon || '', items,
  };
};

const eventItems = (items) => items.map(({ key, ...item }) => item);
const basketDelta = (before, after, event) => {
  const source = event === 'remove_from_cart' ? before : after;
  const other = event === 'remove_from_cart' ? after : before;
  return source.items.map((item) => ({
    ...item,
    quantity: item.quantity - other.items.filter((entry) => entry.key === item.key)
      .reduce((sum, entry) => sum + entry.quantity, 0),
  })).filter((item) => item.quantity > 0);
};

const getOrderData = () => {
  const node = document.querySelector('[data-order-list]');
  if (!node?.dataset.ga4Order) return undefined;
  try {
    const data = JSON.parse(node.dataset.ga4Order);
    if (!data || typeof data.transaction_id !== 'string' || !data.transaction_id.trim()
      || !/^[A-Z]{3}$/.test(data.currency) || !Number.isFinite(data.value) || data.value < 0
      || !Number.isFinite(data.shipping) || data.shipping < 0
      || !Number.isFinite(data.tax) || data.tax < 0
      || !Array.isArray(data.items) || !data.items.length || !data.items.every(validItem)
      || Math.abs(sumItems(data.items) - data.value) > 0.01) return undefined;
    return data;
  } catch (error) {
    return undefined;
  }
};

const pushViewItem = (push, dataset) => {
  // Read numeric tail rather than splitting the product name at its commas.
  const match = (dataset.ga4ProductInfo || '').match(/^([^,]+), (.*), ([\d.,\s]+), ([A-Z]{3}), ([\d.]+)(?:, (.*))?$/);
  if (!match) return;
  const [, itemId, itemName, price, currency, quantity, itemCategory] = match;
  const item = {
    item_id: itemId,
    item_name: itemName,
    price: netPrice(price, dataset.ga4TaxRate),
    quantity: toNumber(quantity),
  };
  if (!validItem(item)) return;
  if (itemCategory) item.item_category = itemCategory;
  push({ event: 'view_item', ecommerce: { currency, value: sumItems([item]), items: [item] } });
};

// Named export is shared by existing storefront imports.
// eslint-disable-next-line import/prefer-default-export
export const createGA4Event = (pushToDataLayer) => {
  const sentPurchases = new Set();
  const pending = new Map();
  return (e) => {
    if (e) {
      const target = e.delegateTarget || e.target?.closest?.('[data-ga4-event]') || e.target;
      const dataset = target?.dataset || {};
      const event = dataset.ga4Event;
      if (event === 'view_item') {
        pushViewItem(pushToDataLayer, dataset);
        return;
      }
      if (!['add_to_cart', 'remove_from_cart', 'add_payment_info', 'add_shipping_info'].includes(event)) return;
      // Coalesce clicks awaiting the same refresh so the same change is not reported twice.
      if (pending.has(event)) return;
      const before = getBasketData();
      const initialMarkup = before.node?.outerHTML;
      const started = Date.now();
      const timer = setInterval(() => {
        const after = getBasketData();
        if (after.node === before.node && after.node?.outerHTML === initialMarkup) {
          if (Date.now() - started >= 15000) {
            clearInterval(timer);
            pending.delete(event);
          }
          return;
        }
        clearInterval(timer);
        pending.delete(event);
        const changed = ['add_to_cart', 'remove_from_cart'].includes(event);
        const items = changed ? basketDelta(before, after, event) : after.items;
        const currency = after.currency || before.currency;
        if (!items.length || !items.every(validItem) || !/^[A-Z]{3}$/.test(currency)) return;
        const ecommerce = {
          currency, coupon: after.coupon, value: sumItems(items), items: eventItems(items),
        };
        if (event === 'add_payment_info') ecommerce.payment_type = dataset.ga4PaymentType || '';
        if (event === 'add_shipping_info') ecommerce.shipping_tier = dataset.ga4ShippingTier || '';
        pushToDataLayer({ event, ecommerce });
      }, 100);
      pending.set(event, timer);
      return;
    }

    const page = document.querySelector('[data-page]');
    if (!page) return;
    if (page.dataset.page === 'view_item') {
      pushViewItem(pushToDataLayer, page.dataset);
    } else if (page.dataset.page === 'begin_checkout') {
      const basket = getBasketData();
      if (basket.items.length && basket.items.every(validItem) && /^[A-Z]{3}$/.test(basket.currency)) {
        pushToDataLayer({
          event: 'begin_checkout',
          ecommerce: {
            currency: basket.currency,
            coupon: basket.coupon,
            value: sumItems(basket.items),
            items: eventItems(basket.items),
          },
        });
      }
    } else if (page.dataset.page === 'purchase') {
      const order = getOrderData();
      if (!order) return;
      const key = `purchaseSent_${order.transaction_id}`;
      if (sentPurchases.has(key)) { captureGa4PurchaseContext(); return; }
      try {
        if (sessionStorage.getItem(key)) { captureGa4PurchaseContext(); return; }
      } catch (error) {
        // Storage restrictions must not prevent a valid purchase event.
      }
      const email = (document.querySelector('[data-order-list]').dataset.enhancedConversionEmail || '').trim().toLowerCase();
      pushToDataLayer({ event: 'purchase', ecommerce: order, ...(email ? { user_data: { email } } : {}) });
      captureGa4PurchaseContext(true);
      sentPurchases.add(key);
      try {
        sessionStorage.setItem(key, 'true');
      } catch (error) {
        // In-memory suppression and GA4 transaction_id deduplication still apply.
      }
    }
  };
};
